import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import http from "../../api/apiClient";
import { AuthContext } from "../../AuthProvider";
import { useTranslation } from "react-i18next";
import ErrorPage from "../../components/ErrorPage";
import Spinner from "../../components/Spinner";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { FaEye, FaEyeSlash, FaRegCopy, FaUserPlus } from "react-icons/fa";
import { UserHero, UserPageShell } from "./components";

import BackButton, { useLeaveForm } from "../../components/navigation/BackButton";
type NewUserForm = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  enabled: boolean;
  preferred_language: string;
};

const generatePassword = () =>
  Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-6).toUpperCase();

const UserNew = () => {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();

  const [user, setUser] = useState<NewUserForm>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: generatePassword(),
    enabled: true,
    preferred_language: "nl",
  });
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshingRoles, setRefreshingRoles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<any>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const labelClass = "text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
  const inputClass =
    "mt-1 w-full rounded-2xl border border-gray-200/70 bg-white/80 px-3 py-2 text-gray-900 shadow-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100";

  const hasFetchedRolesRef = useRef(false);

  const fetchRoles = useCallback(async () => {
    const isInitialFetch = !hasFetchedRolesRef.current;
    if (isInitialFetch) {
      setLoading(true);
    } else {
      setRefreshingRoles(true);
    }
    try {
      const { data } = await http.get("/admin/users/roles");
      const normalized = Array.isArray(data)
        ? Array.from(
            new Set(
              data
                .map((entry) => {
                  if (typeof entry === "string") return entry.trim();
                  if (entry && typeof entry === "object") {
                    const roleEntry = entry as Record<string, string | undefined>;
                    return (
                      roleEntry.name ||
                      roleEntry.role ||
                      roleEntry.id ||
                      roleEntry.key ||
                      ""
                    );
                  }
                  return "";
                })
                .filter(
                  (value): value is string =>
                    typeof value === "string" && Boolean(value.trim())
                )
            ),
          )
        : [];
      setAllRoles(normalized);
      setError(null);
      hasFetchedRolesRef.current = true;
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      if (isInitialFetch) {
        setLoading(false);
      } else {
        setRefreshingRoles(false);
      }
    }
  }, [t]);

  useEffect(() => {
    if (keycloak?.token) {
      fetchRoles();
    }
  }, [keycloak?.token, fetchRoles]);

  const roleOptions = useMemo(
    () =>
      allRoles.filter((role) => !selectedRoles.includes(role)).sort((a, b) => a.localeCompare(b)),
    [allRoles, selectedRoles]
  );

  const handleChange = (e) => {
    setUser((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleToggleStatus = () => {
    setUser((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleCopyPassword = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(user.password);
    }
  };

  const handleAddRole = (role) => {
    if (!role) return;
    setSelectedRoles((prev) => [...prev, role]);
  };

  const handleRemoveRole = (role) => {
    setSelectedRoles((prev) => prev.filter((r) => r !== role));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user.firstName || !user.lastName || !user.username || !user.email) {
      setError(t("admin.users.validation_required", { defaultValue: "Please fill in all required fields." }));
      return;
    }
    try {
      setSaving(true);
      await http.post("/admin/users", {
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.email,
        enabled: user.enabled,
        password: user.password,
        roles: selectedRoles,
        preferred_language: user.preferred_language || "nl",
      });
      leaveForm("/admin/users");
    } catch (err) {
      setError(mapHttpErrorToMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return <ErrorPage message={error} status={error?.status} onRetry={fetchRoles} />;
  }

  const pageTitle = t("admin.users.new.title", { defaultValue: "Create user" });
  const saveLabel = t("common.actions.save", { defaultValue: "Save" });
  const cancelLabel = t("common.actions.cancel", { defaultValue: "Cancel" });
  const firstNameLabel = t("admin.users.first_name", { defaultValue: "First name" });
  const lastNameLabel = t("admin.users.last_name", { defaultValue: "Last name" });
  const usernameLabel = t("admin.users.username", { defaultValue: "Username" });
  const emailLabel = t("admin.users.email", { defaultValue: "Email" });
  const passwordLabel = t("admin.users.password", { defaultValue: "Password" });
  const statusLabel = t("admin.users.status", { defaultValue: "Status" });
  const rolesLabel = t("admin.users.roles", { defaultValue: "Roles" });
  const availableRolesLabel = t("admin.users.available_roles", { defaultValue: "Available roles" });

  return (
    <UserPageShell>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <BackButton fallback="/admin/users" />
      </div>
      <UserHero
        icon={FaUserPlus}
        eyebrow={t("navigation.management", { defaultValue: "Beheer" })}
        title={pageTitle}
        description={t("admin.users.new.description", {
          defaultValue: "Create a staff account and assign initial roles.",
        })}
      />
      <form
        onSubmit={handleSubmit}
        className="lds-section-enter space-y-6 rounded-[24px] border border-slate-200 bg-white p-5 text-slate-950 shadow-sm dark:border-white/10 dark:bg-slate-900/80 dark:text-white sm:p-7"
      >
        <section className="space-y-4">
          {refreshingRoles && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("admin.users.refreshing_roles", { defaultValue: "Updating available roles…" })}
            </p>
          )}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("admin.users.info_section", { defaultValue: "Basic information" })}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                {firstNameLabel}
              </label>
              <input
                type="text"
                name="firstName"
                value={user.firstName}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                {lastNameLabel}
              </label>
              <input
                type="text"
                name="lastName"
                value={user.lastName}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                {usernameLabel}
              </label>
              <input
                type="text"
                name="username"
                value={user.username}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                {emailLabel}
              </label>
              <input
                type="email"
                name="email"
                value={user.email}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>
                {passwordLabel}
              </label>
              <div className="mt-1 flex items-center rounded-2xl border border-gray-200/70 bg-white/80 px-3 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-900/60">
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="password"
                  value={user.password}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-sm focus:outline-none dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={handleCopyPassword}
                  className="ml-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
                  title={t("common.actions.copy", { defaultValue: "Copy" })}
                >
                  <FaRegCopy size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setPasswordVisible((prev) => !prev)}
                  className="ml-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                  title={passwordVisible ? t("common.actions.hide", { defaultValue: "Hide" }) : t("common.actions.show", { defaultValue: "Show" })}
                >
                  {passwordVisible ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("admin.users.password_hint", { defaultValue: "You can adjust the temporary password before saving." })}
              </p>
            </div>
            <div className="flex flex-col">
              <label className={labelClass}>
                {statusLabel}
              </label>
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={user.enabled}
                  onChange={handleToggleStatus}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
                />
                {user.enabled
                  ? t("common.badges.active", { defaultValue: "Active" })
                  : t("common.badges.inactive", { defaultValue: "Inactive" })}
              </label>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{rolesLabel}</h2>
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400" htmlFor="role-select">
              {availableRolesLabel}
            </label>
            <select
              id="role-select"
              onChange={(e) => handleAddRole(e.target.value)}
              value=""
              className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100"
            >
              <option value="">{t("admin.users.select_role", { defaultValue: "Select role" })}</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div className="rounded-2xl border border-indigo-100/70 bg-white/80 dark:border-indigo-500/40 dark:bg-gray-900/40">
            {selectedRoles.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                {t("admin.users.no_roles_selected", { defaultValue: "No roles selected." })}
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {selectedRoles.map((role) => (
                  <li
                    key={role}
                    className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-200"
                  >
                    <span>{role}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveRole(role)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                    >
                      {t("admin.users.remove_role", { defaultValue: "Remove role" })}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{typeof error === "string" ? error : error?.message}</p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="lumen-glow-sheen inline-flex items-center rounded-full border border-gray-200/70 bg-white/80 px-5 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-200"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="lumen-glow-sheen inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-6 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? t("common.loading", { defaultValue: "Loading…" }) : saveLabel}
          </button>
        </div>
      </form>
    </UserPageShell>
  );
};

export default UserNew;
